import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Paper,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import axios from 'axios';

/**
 * Componente de diálogo para actualizar el estado de una orden
 * @param {boolean} open - Si el diálogo está abierto
 * @param {function} onClose - Función para cerrar el diálogo
 * @param {object} orden - Datos de la orden a actualizar
 * @param {function} onUpdateSuccess - Callback al actualizar exitosamente
 * @param {array} estados - Lista de estados disponibles
 */
const ActualizarEstadoDialog = ({ open, onClose, orden, onUpdateSuccess, estados = [] }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 16));
  const [glosa, setGlosa] = useState('');
  const { user } = useAuth();

  // Estados bloqueados que NO permiten cambios (IDs de tp_estado_servicio)
  const ESTADOS_BLOQUEADOS = [9, 10]; // 9: Revisado, 10: Liquidado

  // Verificar si el estado actual está bloqueado
  const estadoBloqueado = orden?.id_estado_servicio && ESTADOS_BLOQUEADOS.includes(parseInt(orden.id_estado_servicio));

  // Log para debug
  useEffect(() => {
    if (open && orden) {
      console.log('🔍 ActualizarEstadoDialog - Orden:', orden);
      console.log('🔍 Estado actual (id_estado_servicio):', orden.id_estado_servicio);
      console.log('🔍 ¿Estado bloqueado?:', estadoBloqueado);
    }
  }, [open, orden, estadoBloqueado]);

  // Debug: Log estados recibidos en el diálogo
  useEffect(() => {
  //  console.log('ActualizarEstadoDialog - Estados recibidos:', estados);
  //  console.log('ActualizarEstadoDialog - Cantidad:', estados.length);
  //  console.log('ActualizarEstadoDialog - Open:', open);
  }, [estados, open]);

  // Reset form cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      setNuevoEstado('');
      setFecha(new Date().toISOString().slice(0, 16));
      setGlosa('');
      setSelectedFile(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!nuevoEstado) {
      alert('Por favor seleccione un estado');
      return;
    }

    const formData = new FormData();
    formData.append('estado', nuevoEstado);
    formData.append('fecha_real', fecha);
    formData.append('glosa', glosa);

    if (selectedFile) {
      formData.append('imagen', selectedFile);
    }

    try {
      const response = await axios.put(
        `${config.API_BASE_URL}/api/ordenes/${orden.id_orden_servicio}/estado`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        onUpdateSuccess && onUpdateSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      alert('Error al actualizar el estado');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Paper sx={{
        borderRadius: 3,
        overflow: 'hidden'
      }}>
        {/* Header del dialog */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          p: 3
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Actualizar Estado de Ordenx
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: '1px solid rgba(26, 35, 126, 0.2)',
                background: 'rgba(79, 195, 247, 0.05)'
              }}>
                <Typography><strong>Orden #:</strong> {orden?.id_orden_servicio}</Typography>
                <Typography><strong>Cliente:</strong> {orden?.nombre_cliente}</Typography>
                <Typography><strong>Local:</strong> {orden?.nombre_local}</Typography>
              </Paper>
            </Grid>

            {/* Mensaje de bloqueo si el estado es Revisado o Liquidado */}
            {estadoBloqueado && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  icon={<LockIcon />}
                  sx={{
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      color: '#ED6C02'
                    }
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Estado Bloqueado
                  </Typography>
                  <Typography variant="body2">
                    Esta orden se encuentra en estado <strong>{estados.find(e => e.id === parseInt(orden.id_estado_servicio))?.nombre || 'bloqueado'}</strong>.
                    Los cambios de estado solo pueden realizarse desde la aplicación móvil.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth disabled={estadoBloqueado}>
                <InputLabel>Nuevo Estado</InputLabel>
                <Select
                  value={nuevoEstado}
                  onChange={(e) => setNuevoEstado(e.target.value)}
                  label="Nuevo Estado"
                  sx={{ borderRadius: 2 }}
                  disabled={estadoBloqueado}
                >
                  <MenuItem value="">Seleccione nuevo estado</MenuItem>
                  {estados.length === 0 && console.log('ADVERTENCIA: Array estados está vacío en Select')}
                  {estados.map((estado) => {
           //         console.log('Mapeando estado:', estado);
                    return (
                      <MenuItem
                        key={estado.id}
                        value={estado.id}
                        sx={{
                          '&:before': {
                            content: '""',
                            display: 'inline-block',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: estado.color,
                            marginRight: '8px'
                          }
                        }}
                      >
                        {estado.nombre}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="datetime-local"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                fullWidth
                label="Fecha y hora"
                InputLabelProps={{ shrink: true }}
                disabled={estadoBloqueado}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comentarios"
                multiline
                rows={3}
                value={glosa}
                onChange={(e) => setGlosa(e.target.value)}
                disabled={estadoBloqueado}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                border: '2px dashed rgba(26, 35, 126, 0.3)',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                opacity: estadoBloqueado ? 0.5 : 1,
                pointerEvents: estadoBloqueado ? 'none' : 'auto'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  style={{ width: '100%' }}
                  disabled={estadoBloqueado}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Adjuntar imagen (opcional)
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 2,
                borderTop: '1px solid rgba(26, 35, 126, 0.1)'
              }}>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderColor: '#1A237E',
                    color: '#1A237E',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#0D47A1',
                      backgroundColor: 'rgba(26, 35, 126, 0.04)'
                    }
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={estadoBloqueado}
                  sx={{
                    background: estadoBloqueado
                      ? '#E0E0E0'
                      : 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                    borderRadius: 2,
                    '&:hover': {
                      background: estadoBloqueado
                        ? '#E0E0E0'
                        : 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(0, 0, 0, 0.38)'
                    }
                  }}
                >
                  Guardar Estado
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default ActualizarEstadoDialog;
